import type { LocaleCode } from '@/i18n/locales'

/**
 * What the assistant says while it is meeting somebody for the first time.
 *
 * This sits beside the flow rather than in `src/i18n`, and that is a narrowing
 * rather than an oversight. These lines are the onboarding conversation
 * itself: they change when the flow changes, they mean nothing anywhere else,
 * and folding forty keys into the shared `Copy` type would make every edit to
 * this script a merge conflict with anybody translating an unrelated screen.
 * Nothing is given up by moving them: `script.test.ts` runs the same parity,
 * emptiness and placeholder checks that make `src/i18n/copy.test.ts` worth
 * having.
 *
 * No line here names the assistant, and that is deliberate rather than an
 * omission. Namu is the product, and the persona a person is talking to says
 * its own name, from the backend, in the words and the grammatical gender that
 * belong to it. A name written into this file would be the device making a
 * second claim about who is talking, which stops being true the day somebody
 * moves the picker in Settings. `src/persona/personas.ts` is where the names
 * live, the only thing that reads them is that picker, and
 * `houseRules.test.ts` fails the build if one appears here.
 *
 * What is deliberately not here is the greeting for somebody coming back.
 * `src/greeting` owns that, and it reads the elapsed time, the clock and the
 * last topic to do it. This file covers one conversation only: the first one.
 *
 * Written to be heard rather than read. Short clauses, no parentheses, and no
 * punctuation a synthesiser turns into a pause of unpredictable length. Every
 * line that can be spoken twice has at least two phrasings, because a step
 * asked again in exactly the same words sounds like a machine that did not
 * notice it had already asked.
 */

/**
 * The opening line, and the idea this design cares about most.
 *
 * Every voice product in this space opens with a language menu: press one for
 * Hausa, press two for English. That menu is the first thing a person meets
 * and it is a form. This greets in both languages instead and lets the reply
 * decide, so the first thing anybody meets is somebody talking to them.
 *
 * It is one string rather than a per-locale line because at this moment there
 * is no locale yet, which is the whole point. It also has no variants: it is
 * heard once per install and never retried, since a second attempt at it would
 * be a language menu with extra steps.
 */
export const BILINGUAL_OPEN =
  'Sannu. Hello. Ka yi magana da yarenka. Speak to me in your own language.'

export interface OnboardingScript {
  /**
   * The disclosure, in four points, spoken in order and shown on screen at the
   * same time.
   *
   * Both, and the reasoning is worth restating because it is the kind of thing
   * that gets simplified away: a spoken yes is a valid signature but a spoken
   * disclosure is not a valid record, and a written disclosure nobody reads
   * aloud is no disclosure at all to somebody using this by voice or with a
   * screen reader.
   */
  consentPoints: readonly string[]
  consentAsk: readonly string[]
  consentAccepted: readonly string[]
  consentDeclined: readonly string[]
  /** For somebody who deferred it or never answered, rather than refused. */
  consentUnasked: readonly string[]
  askName: readonly string[]
  confirmName: readonly string[]
  correctName: readonly string[]
  /** The read-back for anything that is not a name. */
  confirmHeard: readonly string[]
  /** The first sentence after the name is settled, so the name is used in it. */
  inviteNamed: readonly string[]
  invite: readonly string[]
  askPlace: readonly string[]
  placeThanks: readonly string[]
  acknowledge: readonly string[]
  skipped: readonly string[]
  didNotCatch: readonly string[]
  stillThere: readonly string[]
  typedFallback: readonly string[]
  finished: readonly string[]
}

const en: OnboardingScript = {
  consentPoints: [
    'This is Namu. I answer out loud, and I will sometimes be wrong, so check anything that matters.',
    'While you talk, your voice is turned into words so I can understand you. The recording is not kept and it is never used to teach anything.',
    'If you agree, I keep what you tell me, so that next time I already know you.',
    'You can hear everything I have kept, change it, or tell me to forget all of it, whenever you like.',
  ],
  consentAsk: ['May I keep what you tell me?', 'So, may I remember you? Yes or no.'],
  consentAccepted: ['Thank you. I will remember.', 'Good. I will keep that.'],
  consentDeclined: [
    'That is fine. I will not keep anything, and I can still help you.',
    'Understood. Nothing is kept, and I can still answer your questions.',
  ],
  consentUnasked: [
    'I will not keep anything for now. I can ask another day.',
    'We can leave that for another time. Nothing is kept.',
  ],
  askName: ['What should I call you?', 'What name should I use for you?'],
  confirmName: ['{name}. Have I got that right?', 'I heard {name}. Is that right?'],
  correctName: ['Sorry. Say your name once more for me.', 'Let me try again. What is your name?'],
  confirmHeard: ['{value}. Is that right?', 'I heard {value}. Have I got that?'],
  inviteNamed: ['{name}, ask me anything.', 'Go ahead, {name}. Ask me anything you like.'],
  invite: ['Ask me anything.', 'Go ahead. Ask me anything you like.'],
  askPlace: [
    'One more thing, and it helps. Where are you? What I can tell you about a clinic or a market is different in Kano and in Sokoto.',
    'Where are you speaking from? Answers about a clinic or a market change between Kano and Sokoto.',
  ],
  placeThanks: ['{place}. I will remember that.', 'Noted, {place}.'],
  acknowledge: ['Got it.', 'Thank you.'],
  skipped: ['That is fine.', 'We will leave it.'],
  didNotCatch: ['Sorry, I did not catch that.', 'I missed that.'],
  stillThere: ['Are you still there?', 'I am still here when you are ready.'],
  typedFallback: [
    'If it is hard to hear me, you can type it instead.',
    'It may be loud where you are. You can type your answer.',
  ],
  finished: ['Thank you. Now, ask me anything.', 'That is everything. Go ahead, I am listening.'],
}

/**
 * Hausa, Boko orthography, Standard Kano variety.
 *
 * NEEDS NATIVE REVIEW, on the same terms as `src/i18n/ha.ts`: the locale is
 * marked `reviewed: false` and shown as a preview, so the claim the product
 * makes matches the work actually done. Second-person forms use the masculine
 * `ka`, which is the common written default and the choice the rest of the
 * app already made.
 */
const ha: OnboardingScript = {
  consentPoints: [
    'Wannan Namu ne. Ina amsa da murya, kuma wani lokaci zan yi kuskure, don haka ka tabbatar da duk abin da ke da muhimmanci.',
    'Yayin da kake magana, ana juya muryarka zuwa rubutu don in fahimce ka. Ba a ajiye sautin, kuma ba a amfani da shi wajen koyar da komai.',
    'Idan ka yarda, zan tuna abin da ka gaya mini, don a karo na gaba na riga na san ka.',
    'Za ka iya jin duk abin da na ajiye, ka canza shi, ko ka ce in manta da shi, a duk lokacin da kake so.',
  ],
  consentAsk: ['Zan iya ajiye abin da ka gaya mini?', 'To, zan iya tuna da kai? Ee ko a’a.'],
  consentAccepted: ['Na gode. Zan tuna.', 'Madalla. Zan ajiye shi.'],
  consentDeclined: [
    'Ba laifi. Ba zan ajiye komai ba, kuma har yanzu zan iya taimaka maka.',
    'Na ji. Ba a ajiye komai, kuma har yanzu zan amsa tambayoyinka.',
  ],
  consentUnasked: [
    'Ba zan ajiye komai a yanzu ba. Zan iya tambaya wata rana.',
    'Za mu bar wannan zuwa wani lokaci. Ba a ajiye komai.',
  ],
  askName: ['Me zan kira ka?', 'Wane suna zan yi amfani da shi a gare ka?'],
  confirmName: ['{name}. Haka ne?', 'Na ji {name}. Daidai ne?'],
  correctName: ['Yi haƙuri. Ka sāke faɗa mini sunanka.', 'Bari in sāke gwadawa. Yaya ake kiran ka?'],
  confirmHeard: ['{value}. Daidai ne?', 'Na ji {value}. Haka ne?'],
  inviteNamed: ['{name}, ka tambaye ni duk abin da kake so.', 'Ci gaba, {name}. Tambaye ni komai.'],
  invite: ['Ka tambaye ni duk abin da kake so.', 'Ci gaba. Tambaye ni komai.'],
  askPlace: [
    'Abu ɗaya kuma, kuma yana taimakawa. A ina kake? Abin da zan iya gaya maka game da asibiti ko kasuwa ya bambanta a Kano da a Sakkwato.',
    'Daga ina kake magana? Amsoshi game da asibiti ko kasuwa sun bambanta tsakanin Kano da Sakkwato.',
  ],
  placeThanks: ['{place}. Zan tuna da hakan.', 'Na ji, {place}.'],
  acknowledge: ['Na ji.', 'Na gode.'],
  skipped: ['Ba laifi.', 'Za mu bar shi.'],
  didNotCatch: ['Yi haƙuri, ban ji ba.', 'Ban ji sosai ba.'],
  stillThere: ['Kana nan?', 'Ina nan idan ka shirya.'],
  typedFallback: [
    'Idan yana da wuya a ji ni, za ka iya rubuta shi maimakon.',
    'Wataƙila akwai hayaniya inda kake. Za ka iya rubuta amsarka.',
  ],
  finished: ['Na gode. Yanzu, ka tambaye ni komai.', 'Shi ke nan. Ci gaba, ina saurare.'],
}

export const ONBOARDING_SCRIPT: Record<LocaleCode, OnboardingScript> = { en, ha }
